import { NgModule } from '@angular/core'
import { Routes, RouterModule, TitleStrategy } from '@angular/router'
import { SeoTitleStrategy } from './services/seo.service'

import { NavComponent } from './nav/nav.component'
import { StatusComponent } from './status/status.component'
import { CartComponent } from './cart/cart.component'
import { SampleFormComponent } from './sample-form/sample-form.component'
import { AgentDashboardComponent } from './agent-dashboard/agent-dashboard.component'
import { AppComponent } from './app.component'
import { FeedComponent } from './feed/feed.component'
import { LoginComponent } from './login/login.component'
import { SignupComponent } from './signup/signup.component'
import { TestComponent } from './test/test.component'
import {
    CanExitGuard,
    AuthenticationService,
} from './services/authentication.service'
import { WaitlistComponent } from './waitlist/waitlist.component'
import { ProfileComponent } from './profile/profile.component'
import { BasicAuthGuard } from './guards/basic-auth.guard'
import { InLocalGuard } from './guards/in-local.guard'
import { PublicProfileComponent } from './public-profile/public-profile.component'
import { MessagesComponent } from './messages/messages.component'
import { ProfilePageResolver } from './resolvers/profile-page.resolver'
import { MessagesResolver } from './resolvers/messages.resolver'
import { SearchPageComponent } from './search-page/search-page.component'
import { HomePageComponent } from './home-page/home-page.component'
import { AboutUsComponent } from './about-us/about-us.component'
import { VerifyEmailComponent } from './verify-email/verify-email.component'
import { ForgotPasswordComponent } from './forgot-password/forgot-password.component'
import { ResetPasswordComponent } from './reset-password/reset-password.component'
import { TeamPageComponent } from './team-page/team-page.component'
import { PpaPageComponent } from './ppa-page/ppa-page.component'
import { SalesPageComponent } from './sales-page/sales-page.component'
import { HousesPageComponent } from './houses-page/houses-page.component'
import { AdminLoginComponent } from './admin/admin-login/admin-login.component'
import { AdminLayoutComponent } from './admin/admin-layout/admin-layout.component'
import { AdminDashboardComponent } from './admin/admin-dashboard/admin-dashboard.component'
import { AdminUsersComponent } from './admin/admin-users/admin-users.component'
import { AdminPostsComponent } from './admin/admin-posts/admin-posts.component'
import { AdminChatsComponent } from './admin/admin-chats/admin-chats.component'
import { AdminReferralsComponent } from './admin/admin-referrals/admin-referrals.component'
import { AdminPpasComponent } from './admin/admin-ppas/admin-ppas.component'
import { AdminAuthGuard } from './guards/admin-auth.guard'
import { PublicSaleComponent } from './public-sale/public-sale.component'
import { PublicAccommodationComponent } from './public-accommodation/public-accommodation.component'
import { PublicPpaComponent } from './public-ppa/public-ppa.component'
import { AmbassadorComponent } from './ambassador/ambassador.component'

const routes: Routes = [
    {
        path: '',
        component: HomePageComponent, // no title - it gets the full site title (SeoService DEFAULT_TITLE)
    },
    {
        path: 'index',
        component: HomePageComponent,
        data: { canonicalPath: '/' },
    },
    {
        path: 'landing', // @todo: update routes later!
        component: HomePageComponent,
        data: { canonicalPath: '/' },
    },
    {
        path: 'waitlist',
        title: 'Welcome! Join our wait list.',
        component: WaitlistComponent,
    },
    // TODO: create a holder component, and use nav and router there. So we can use nav in other places.
    {
        path: '',
        component: NavComponent,
        canActivate: [BasicAuthGuard],
        children: [
            // hmmm... not sure how to use this rn, but we need for when we start using the side menu
            {
                path: 'home',
                title: 'Your feed',
                component: FeedComponent,
            },
            {
                path: 'houses',
                title: 'Houses for you',
                component: HousesPageComponent,
            },
            {
                path: 'sales',
                title: 'Sales for you',
                component: SalesPageComponent,
            },
            {
                path: 'profile',
                title: 'Profile',
                component: ProfileComponent,
                resolve: {
                    res: ProfilePageResolver,
                },
            },
            {
                path: 'messages',
                title: 'Messages',
                component: MessagesComponent,
                resolve: {
                    res: MessagesResolver,
                },
            },
            {
                path: 'ppa',
                title: 'PPAs',
                component: PpaPageComponent,
            },
            {
                path: 'dashboard',
                title: 'Dashboard',
                component: AgentDashboardComponent,
                canActivate: [AuthenticationService],
                canDeactivate: [CanExitGuard],
            },
        ],
    },
    {
        path: 'search',
        title: 'Search', //  items and PPAs
        component: SearchPageComponent,
        data: { description: 'Search corpers lodges, items for sale and PPA reviews from NYSC corps members, by state and LGA.' },
        // TODO: they won't be able to comment or leave a review if not signed in.
    },
    {
        path: 'p/:route-id',
        title: 'Hey!',
        canActivate: [AuthenticationService],
        component: PublicProfileComponent,
    },
    {
        path: 's/:id',
        component: PublicSaleComponent,
    },
    {
        path: 'a/:id',
        component: PublicAccommodationComponent,
    },
    {
        path: 'ppa/:id',
        component: PublicPpaComponent,
    },

    // put activatedRoute for dashboard, agents much be loggedIN

    // People searching "corps member login" / "corp portal" often want the NYSC portal - say plainly this isn't it.
    { path: 'login', title: 'Log in', component: LoginComponent, data: { description: "Log in to corpers.ng, the independent community for corps members: lodges, PPA reviews and chat. Looking for the NYSC portal? That's portal.nysc.org.ng." } },
    { path: 'signup', title: 'Sign up', component: SignupComponent, data: { description: 'Join corpers.ng - the independent community for NYSC corps members. Find a corpers lodge, buy and sell, and review PPAs.' } },
    { path: 'verify-email', title: 'Verify your email', component: VerifyEmailComponent },
    { path: 'forgot-password', title: 'Forgot password', component: ForgotPasswordComponent },
    { path: 'reset-password', title: 'Reset password', component: ResetPasswordComponent },
    { path: 'about', title: 'About us', component: AboutUsComponent, data: { description: 'corpers.ng is an independent platform that helps NYSC corps members find accommodation, buy and sell items, and connect with peers.' } },
    { path: 'ambassador', title: 'Become an Ambassador', component: AmbassadorComponent, data: { description: 'Become a corpers.ng ambassador and help corps members in your state find accommodation, sell items and connect.' } },
    { path: 'team', title: 'Meet the Team', component: TeamPageComponent, data: { description: 'Meet the team building corpers.ng, the independent community platform for NYSC corps members.' } },
    { path: 'admin/login', title: 'Admin login', component: AdminLoginComponent },
    {
        path: 'admin',
        component: AdminLayoutComponent,
        canActivate: [AdminAuthGuard],
        children: [
            { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
            { path: 'dashboard', title: 'Admin dashboard', component: AdminDashboardComponent },
            { path: 'users', title: 'Admin users', component: AdminUsersComponent },
            { path: 'posts', title: 'Admin posts', component: AdminPostsComponent },
            { path: 'ppas', title: 'Admin PPAs', component: AdminPpasComponent },
            { path: 'chats', title: 'Admin chats', component: AdminChatsComponent },
            { path: 'referrals', title: 'Admin referrals', component: AdminReferralsComponent },
        ],
    },
    {
        path: 'form',
        title: 'A sample Form Page.',
        component: SampleFormComponent,
        canActivate: [InLocalGuard],
    },
    {
        path: 'test',
        title: 'This is a test component',
        component: TestComponent,
        canActivate: [InLocalGuard],
    },
    {
        path: 'status',
        title: 'Status',
        component: StatusComponent,
        canActivate: [InLocalGuard],
    },
    {
        path: 'cart',
        title: 'Cart',
        component: CartComponent,
        canActivate: [InLocalGuard],
    },

    // must be last
    { path: '**', redirectTo: '' },
]

@NgModule({
    imports: [
        RouterModule.forRoot(routes, {
            // A new page opens at the top, Back returns to where you were, and a link's #fragment scrolls to it.
            // Only the window scrolls here: pages inside NavComponent scroll in its own slot (see CLAUDE.md).
            scrollPositionRestoration: 'enabled',
            anchorScrolling: 'enabled',
            scrollOffset: [0, 96], // clears the public pages' sticky header (<app-site-header>), like their scroll-mt-24
        }),
    ],
    exports: [RouterModule],
    providers: [{ provide: TitleStrategy, useClass: SeoTitleStrategy }],
})
export class AppRoutingModule {}
